import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import AppHeader from "@/components/AppHeader";
import { Trash2, UserPlus, Mail, Phone, CheckCircle2, Clock, Send } from "lucide-react";

export default function Beneficiaries() {
  const [list, setList] = useState([]);
  const [name, setName] = useState("");
  const [rel, setRel] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/beneficiaries");
    setList(data);
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      await api.post("/beneficiaries", { name, relationship: rel, email, phone });
      setName(""); setRel(""); setEmail(""); setPhone("");
      load();
    } catch (e) {
      setError(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    await api.delete(`/beneficiaries/${id}`);
    load();
  };

  const resend = async (id) => {
    setError("");
    try {
      const r = await api.post(`/beneficiaries/${id}/resend-confirmation`);
      if (!r.data?.email_sent) setError(r.data?.error || "Email could not be sent.");
      load();
    } catch (e) {
      setError(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="min-h-screen bg-cream">
      <AppHeader />
      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Beneficiaries</div>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-2">Who takes the keys.</h1>
        <p className="text-moss mt-3 max-w-2xl leading-relaxed">
          If anything happens to you, these trusted contacts can claim your account. We verify them
          by their <span className="font-medium text-ink">email</span> and{" "}
          <span className="font-medium text-ink">phone number</span> — no one else can request access on their behalf.
        </p>

        <div className="grid lg:grid-cols-12 gap-8 mt-12">
          {/* Add form */}
          <form onSubmit={add} className="lg:col-span-5 bg-linen border border-sand rounded-2xl p-8 h-fit" data-testid="add-beneficiary-form">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Add</div>
            <h2 className="font-serif text-2xl mt-2">Trusted contact</h2>
            <div className="mt-6 space-y-4">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Full name</label>
                <input type="text" required value={name} onChange={(e) => setName(e.target.value)}
                  className="mt-2 w-full bg-cream border border-sand rounded-lg px-4 py-3 focus:ring-2 focus:ring-forest focus:border-transparent outline-none"
                  placeholder="Jane Doe" data-testid="bene-name" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Relationship</label>
                <input type="text" required value={rel} onChange={(e) => setRel(e.target.value)}
                  className="mt-2 w-full bg-cream border border-sand rounded-lg px-4 py-3 focus:ring-2 focus:ring-forest focus:border-transparent outline-none"
                  placeholder="Spouse, child, sibling…" data-testid="bene-rel" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Email (verification)</label>
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  className="mt-2 w-full bg-cream border border-sand rounded-lg px-4 py-3 focus:ring-2 focus:ring-forest focus:border-transparent outline-none"
                  placeholder="jane@example.com" data-testid="bene-email" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Phone (verification)</label>
                <input type="tel" required value={phone} onChange={(e) => setPhone(e.target.value)}
                  className="mt-2 w-full bg-cream border border-sand rounded-lg px-4 py-3 font-mono focus:ring-2 focus:ring-forest focus:border-transparent outline-none"
                  placeholder="+1 555 123 4567" data-testid="bene-phone" />
              </div>
              {error && <div className="text-sm text-clay" data-testid="bene-error">{error}</div>}
              <button type="submit" disabled={busy}
                className="w-full bg-forest text-white rounded-lg px-6 py-3.5 font-medium hover:bg-forest-deep disabled:opacity-60 transition-colors inline-flex items-center justify-center gap-2"
                data-testid="bene-submit">
                <UserPlus className="w-4 h-4" strokeWidth={1.5} />
                {busy ? "Adding…" : "Add trusted contact"}
              </button>
              <p className="text-xs text-sage leading-relaxed">
                Both email and phone are required for posthumous access claims. Both must verify a 2-factor challenge.
              </p>
            </div>
          </form>

          {/* List */}
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage mb-4">Trusted roster</div>
            {list.length === 0 ? (
              <div className="border border-dashed border-sand rounded-2xl p-12 text-center text-sage" data-testid="bene-empty">
                No trusted contacts yet. Add your first to the left.
              </div>
            ) : (
              <div className="space-y-3" data-testid="bene-list">
                {list.map((b) => (
                  <div key={b.id} className="bg-linen border border-sand rounded-xl p-5 flex items-start justify-between gap-4" data-testid={`bene-${b.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-serif text-xl text-ink">{b.name}</div>
                        {b.confirmed ? (
                          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] font-semibold px-2 py-0.5 rounded-full bg-forest text-white" data-testid={`bene-${b.id}-confirmed`}>
                            <CheckCircle2 className="w-3 h-3" /> Confirmed
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] font-semibold px-2 py-0.5 rounded-full bg-clay-light text-clay" data-testid={`bene-${b.id}-pending`}>
                            <Clock className="w-3 h-3" /> Pending
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-moss mb-3">{b.relationship}</div>
                      <div className="space-y-1 text-sm">
                        <div className="flex items-center gap-2 text-ink">
                          <Mail className="w-3.5 h-3.5 text-sage" strokeWidth={1.5} />
                          <span className="font-mono text-xs truncate">{b.email}</span>
                        </div>
                        <div className="flex items-center gap-2 text-ink">
                          <Phone className="w-3.5 h-3.5 text-sage" strokeWidth={1.5} />
                          <span className="font-mono text-xs">{b.phone}</span>
                        </div>
                      </div>
                      {!b.confirmed && (
                        <button onClick={() => resend(b.id)}
                          className="mt-3 inline-flex items-center gap-1.5 text-xs text-forest hover:underline"
                          data-testid={`bene-${b.id}-resend`}>
                          <Send className="w-3 h-3" /> Resend confirmation email
                        </button>
                      )}
                    </div>
                    <button onClick={() => remove(b.id)}
                      className="text-moss hover:text-clay transition-colors shrink-0"
                      aria-label="Remove" data-testid={`bene-delete-${b.id}`}>
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
